// To Enable Picking
import '@babylonjs/core/Culling/ray';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import {
    Vector3, Vector2, Color4, Color3
} from '@babylonjs/core/Maths/math';
import { IVector3Like } from '@babylonjs/core/Maths/math.like';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { PickingInfo } from '@babylonjs/core/Collisions/pickingInfo';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { VideoTexture } from '@babylonjs/core/Materials/Textures/videoTexture';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Scene } from '@babylonjs/core/scene';
// import { GridMaterial } from '@babylonjs/materials/grid/gridMaterial';

// import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
// import '@babylonjs/core/Materials/Textures/Loaders/envTextureLoader';
// import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';

import remove from 'lodash/remove';

import { SystemEvent } from '../System/system';
import { SystemAnimation } from '../System/systemAnimation';
import { isVideoFile } from '~services/util';

export const FAR = 10000000000;

const setMeshVisibility = (mesh: AbstractMesh, vis: number) => {
    mesh.visibility = vis * vis;
    if (mesh.material && mesh.material.setFloat) {
        mesh.material.setFloat('visibility', vis * vis);
    }
};

const FOG_START = 15;
const FOG_END = 25;
const FOG_DENSITY_AP = 50;
export const DEFAULT_LIGHTROTHEIGHT: IVector3Like = { x: 0, y: 0, z: 1 };

export class SystemCamera extends SystemAnimation {
    public freeCamera: FreeCamera;

    private cameraFixed: FreeCamera;

    private planeFree: Mesh;

    private planeFollow: Mesh;

    private planeFixed: Mesh;

    private cameraZ = -10;

    public textureMouseTrail: Texture;

    constructor(canvas: HTMLCanvasElement) {
        super(canvas);

        this.scene.hoverCursor = 'pointer';
        this.scene.clearColor = new Color4(1, 1, 1, 0);

        this.freeCamera = this.buildCamera('freeCamera');
        this.planeFree = this.buildPickPlane('planeFree');
        // I thought planeFree needed to be fixed but it works without that

        this.cameraFixed = this.buildCamera('cameraFixed');
        this.planeFollow = this.buildPickPlane('planeFollow');
        this.planeFollow.parent = this.freeCamera;
        this.planeFixed = this.buildPickPlane('planeFixed');
        this.planeFixed.parent = this.cameraFixed;
        // this.addHelper();

        this.scene.activeCamera = this.freeCamera;

        this.calculateScenePixelRatio();
        this.on(SystemEvent.Resize, () => {
            this.calculateScenePixelRatio();
        });

        // const cibe = new CubeTexture('', this.scene);
        // cibe.hasAlpha = false;
        // const test = new PBRMaterial('');
        // test.alpha = 0.5;

        this.setMeshVisibilityEvents();
        // this.useSecondCamera();
    }

    private useSecondCamera() {
        this.scene.activeCamera = this.cameraFixed;
        this.cameraFixed.attachControl(this.canvas);
        this.forceRender();
        this.canvas.style.zIndex = 100000000;
    }

    private setMeshVisibilityEvents() {
        let meshesNeedControl: AbstractMesh[] = [];
        setInterval(() => {
            meshesNeedControl = [];
            const camPos = this.freeCamera.globalPosition;
            this.scene.meshes.forEach((mesh) => {
                if (!mesh.alwaysSelectAsActiveMesh) {
                    const dist = Vector3.Distance(mesh.absolutePosition, camPos);
                    if (dist < 10) {
                        meshesNeedControl.push(mesh);
                    } else {
                        setMeshVisibility(mesh, 1);
                    }
                }
            });
        }, 1000);

        this.scene.registerBeforeRender(() => {
            const camPos = this.freeCamera.globalPosition;
            meshesNeedControl.forEach((mesh) => {
                const dist = Vector3.Distance(mesh.absolutePosition, camPos);
                if (dist > 0 && dist < 5) {
                    const vis = Math.max(dist - 4, 0);
                    setMeshVisibility(mesh, vis);
                } else {
                    setMeshVisibility(mesh, 1);
                }
            });
        });
    }

    public setMouseTrailTexture(texture: Texture) {
        this.textureMouseTrail = texture;
        this.notify(SystemEvent.MouseTrailTexture, 0);
    }

    private buildCamera(name: string): FreeCamera {
        const camera = new FreeCamera(name, new Vector3(0, 0, this.cameraZ), this.scene);
        camera.setTarget(Vector3.Zero());
        return camera;
    }

    private buildPickPlane(name: string): Mesh {
        const plane = CreatePlane(name, { width: 10000, height: 10000 }, this.scene);
        plane.position.z = 10;
        plane.isVisible = false;
        plane.isPickable = false;
        return plane;
    }

    // private addHelper() {
    //     this.planeFollow.alwaysSelectAsActiveMesh = true;
    //     this.planeFollow.isVisible = true;
    //     this.planeFollow.visibility = 0.5;
    //     const gridMaterial = new GridMaterial('', this.scene);
    //     this.planeFollow.material = gridMaterial;
    // }

    public htmlParent: HTMLElement;

    public setHtmlParent(htmlParent: HTMLElement) {
        this.htmlParent = htmlParent;
    }

    // private calculateUnitSize(zPosition: number): ISize {
    //     const distance = zPosition - this.cameraZ;
    //     const vFov = this.freeCamera.fov;
    //     const height = 2 * Math.tan(vFov / 2) * distance;
    //     const width = height * this.engine.getAspectRatio(this.freeCamera);
    //     return {
    //         width,
    //         height
    //     };
    // }

    public pickInFrontOfCamera(pos: Vector2): PickingInfo {
        return this.pick(pos, this.cameraFixed, this.planeFree);
    }

    public pickFromPosition(pos: Vector2, pickHtmlPlaneOnly: boolean): PickingInfo {
        if (pickHtmlPlaneOnly) {
            return this.pick(pos, this.cameraFixed, this.planeFixed);
        }
        return this.pick(pos, this.freeCamera);
    }

    public pickFromPositionStatic(pos: Vector2, plane?: Mesh): PickingInfo {
        const planeUsed = plane || this.planeFollow;
        const pick = this.pick(pos, this.freeCamera, planeUsed);
        if (plane && pick.hit) {
            pick.pickedPoint.subtractInPlace(this.freeCamera.globalPosition);
        }
        return pick;
    }

    private pick(pos: Vector2, camera: FreeCamera, plane?: Mesh) {
        if (plane) {
            return this.scene.pick(
                pos.x,
                pos.y,
                (m) => m === plane,
                true,
                camera
            );
        }
        //* Do not use fast check or pick is not precise enough
        // return this.scene.pick(pos.x, pos.y, null, true, camera);
        return this.scene.pick(pos.x, pos.y, null, false, camera);
    }

    public scenePixelRatio = 75;

    public sceneGap = 1;

    private calculateScenePixelRatio() {
        const rect = this.canvas.getBoundingClientRect();
        const topleft = new Vector2(0, 0);
        const topleftPick = this.pickFromPosition(topleft, true);
        if (topleftPick.hit) {
            const pickGap = -topleftPick.pickedPoint.x;
            const pixelHalfWidth = rect.width / 2;
            this.scenePixelRatio = pixelHalfWidth / pickGap;
        }
        const topleftPickStatic = this.pickFromPositionStatic(topleft, this.planeFixed);
        if (topleftPickStatic.hit) {
            this.sceneGap = -topleftPickStatic.pickedPoint.x;
        }
    }

    private _renderAfterLoading = false;

    public set renderAfterLoading(v: boolean) {
        this._renderAfterLoading = v;
    }

    public get renderAfterLoading(): boolean {
        return this._renderAfterLoading;
    }

    public getTexture(
        buffer: string | HTMLVideoElement | HTMLImageElement,
        callback?: (t: Texture) => void,
        invertY?: boolean
    ): Texture {
        let texture: Texture;
        invertY = (invertY) || false;
        const url = (typeof buffer === 'string' || buffer instanceof String) ? buffer as string : buffer.src;
        if (isVideoFile(url)) {
            texture = this.getVideoTexture(buffer, callback, invertY)
        } else {
            texture = this.getImageTexture(buffer, callback, invertY)
        }
        texture.hasAlpha = true;
        return texture;
    }

    private getVideoTexture(
        buffer: string | HTMLVideoElement | HTMLImageElement,
        callback?: (t: Texture) => void,
        invertY?: boolean
    ): Texture {
        const url = (typeof buffer === 'string' || buffer instanceof String) ? buffer as string : buffer.src;
        const bufVideo = buffer as HTMLVideoElement
        //! Keep mipmap set to true or we see lines in shaders
        const texture = new VideoTexture(url, bufVideo, this.scene, true, !invertY,
            Texture.TRILINEAR_SAMPLINGMODE,
        );
        texture.video.pause()
        texture.video.onplaying = () => {
            texture.video.onplaying = () => { };
            texture.video.play();
            this.textureLoaded(texture, callback);

        }
        return texture;
    }

    private getImageTexture(
        buffer: string | HTMLVideoElement | HTMLImageElement,
        callback?: (t: Texture) => void,
        invertY?: boolean
    ): Texture {
        let texture: Texture;
        const url = (typeof buffer === 'string' || buffer instanceof String) ? buffer as string : buffer.src;
        if (typeof buffer === 'string' || buffer instanceof String) {
            texture = new Texture(url, this.scene, true, invertY,
                Texture.TRILINEAR_SAMPLINGMODE,
                () => {
                    this.textureLoaded(texture, callback);
                }
            );
        } else {
            const bufImage = buffer as HTMLImageElement
            texture = new Texture(url, this.scene, true, invertY,
                Texture.TRILINEAR_SAMPLINGMODE,
                () => {
                    this.textureLoaded(texture, callback);
                }, null, bufImage, true);
        }
        return texture;
    }

    private textureLoaded(texture: Texture, callback?: (t: Texture) => void) {
        //! Callback needed when the texture is changed in editor
        if (this._renderAfterLoading && this.launched) {
            this.scene.render()
            this.quickRender(200);
        }
        if (callback) callback(texture);
    }

    public getModel(url: string, callback: (t: Mesh) => void) {
        import('../Modules/loadGltf').then((mod) => {
            mod.getModel(url, this.scene, callback);
        });
    }

    public isMeshCloseToCamera(mesh: TransformNode): boolean {
        const dist = Vector3.Distance(this.freeCamera.globalPosition, mesh.getAbsolutePosition());
        return dist < 40;
    }

    private shaderMaterials: ShaderMaterial[] = [];

    public addShaderMaterial(shaderMaterial: ShaderMaterial) {
        this.shaderMaterials.push(shaderMaterial);
        this.setFogDensity(this.fogDensity);
        this.setFogColor(this.fogColor);
        this.setLightRotHeight(this.lightRotHeight);
    }

    public removeShaderMaterial(shaderMaterial: ShaderMaterial) {
        remove(this.shaderMaterials, (sh) => sh === shaderMaterial);
        shaderMaterial.setFloat('fogStart', FAR);
        shaderMaterial.setFloat('fogEnd', FAR + 1);
        shaderMaterial.setVector3('lightRotHeight', Vector3.One());
    }

    private fogDensity = 0;

    public setFogDensity(fogDensity: number) {
        this.fogDensity = fogDensity;
        const FOG_END_WITH_DENSITY = FOG_END + (1 - fogDensity) * FOG_DENSITY_AP;
        if (fogDensity) {
            this.scene.fogEnabled = true;
            this.scene.fogMode = Scene.FOGMODE_LINEAR;
            this.scene.fogStart = FOG_START;
            this.scene.fogEnd = FOG_END_WITH_DENSITY;
        }
        this.shaderMaterials.forEach((sh) => {
            sh.setFloat('fogStart', FOG_START);
            sh.setFloat('fogEnd', FOG_END_WITH_DENSITY);
            // sh.setFloat('fogDensity', fogDensity);
            // const texture = this.getTexture('https://d2w9uul1fh5o24.cloudfront.net/editor/scrollImages/v2/512_webp/noise_143.webp', null, true);
            // sh.setTexture('textureFog', texture);
            // sh.setColor3('fogColor1', new Color3(0, 0.4, 0));
            // sh.setColor3('fogColor2', new Color3(0.5, 0.4, 0));
        });
    }

    private lightRotHeight = DEFAULT_LIGHTROTHEIGHT;

    public setLightRotHeight(lightRotHeight: IVector3Like) {
        this.lightRotHeight = lightRotHeight;
        const adjustedLightRotHeight = new Vector3(
            lightRotHeight.x * 100,
            lightRotHeight.y * 100,
            lightRotHeight.z,
        );
        this.shaderMaterials.forEach((sh) => {
            sh.setVector3('lightRotHeight', adjustedLightRotHeight);
        });
    }

    private fogColor = Color3.Black();

    public setFogColor(fogColor: Color3) {
        this.fogColor = fogColor;
        this.scene.fogColor = fogColor;
        this.shaderMaterials.forEach((sh) => {
            sh.setColor3('fogColor', fogColor);
        });
    }
}
